/**
 * DemoControlBar – Floating control bar for auto-play demo.
 *
 * Features:
 *  - Play / Pause / Stop buttons
 *  - Speed selector (1x / 2x / 3x)
 *  - Step progress indicator
 *  - Script selector dropdown
 *  - Only renders when VITE_DEMO_MODE is active
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Play,
    Pause,
    Square,
    SkipForward,
    Gauge,
    ChevronDown,
    Monitor,
    Volume2,
    VolumeX,
    Volume1,
    FlaskConical,
} from 'lucide-react';
import { WhatIfPanel } from './WhatIfPanel';
import { Button } from '@/modules/shared/components/ui/button';
import { Badge } from '@/modules/shared/components/ui/badge';
import { useDemo, type DemoSpeed } from '@/modules/shared/contexts/DemoContext';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useDemoAccess } from '@/modules/shared/hooks/useDemoAccess';
import { DEMO_SCRIPTS, getScriptsForRole, type DemoScriptStep } from '@/modules/shared/services/DemoScripts';
import { demoPlayer } from '@/modules/shared/services/DemoPlayerService';
import { demoNarration, type NarrationState } from '@/modules/shared/services/DemoNarrationService';

export function DemoControlBar() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const demo = useDemo();
    const [currentStep, setCurrentStep] = useState<DemoScriptStep | null>(null);
    const [showScriptMenu, setShowScriptMenu] = useState(false);
    const [narrationState, setNarrationState] = useState<NarrationState>(demoNarration.getState());
    const [whatIfOpen, setWhatIfOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Extract programId from URL path since this component renders outside route params
    const programId = useMemo(() => {
        const match = location.pathname.match(/\/programs\/([0-9a-fA-F-]{36})/);
        return match?.[1] ?? undefined;
    }, [location.pathname]);

    // Combined env + per-program demo gate.
    const { isDemoAllowed } = useDemoAccess(programId ?? null);

    const roleName = user?.programRoleName ?? user?.role ?? '';
    const availableScripts = roleName ? getScriptsForRole(roleName) : DEMO_SCRIPTS;

    // ─── Narration state subscription ─────────────────
    useEffect(() => {
        return demoNarration.subscribe(setNarrationState);
    }, []);

    // ─── Stop demo immediately when the program disables it ───
    useEffect(() => {
        if (!isDemoAllowed) {
            demoPlayer.stop();
            demoNarration.stop();
            setCurrentStep(null);
            setShowScriptMenu(false);
            setWhatIfOpen(false);
        }
    }, [isDemoAllowed]);

    // ─── Click outside to close menu ──────────────────
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowScriptMenu(false);
            }
        };
        if (showScriptMenu) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showScriptMenu]);

    // ─── Player callbacks ─────────────────────────────
    const handleStepChange = useCallback(
        (stepIndex: number, step: DemoScriptStep) => {
            setCurrentStep(step);
            demo.goToStep(stepIndex);
        },
        [demo],
    );

    const handleComplete = useCallback(() => {
        demo.stop();
        setCurrentStep(null);
    }, [demo]);

    const handleNavigate = useCallback(
        (route: string) => navigate(route),
        [navigate],
    );

    // ─── Actions ──────────────────────────────────────
    const handlePlay = useCallback(
        (scriptId: string) => {
            const script = DEMO_SCRIPTS.find((s) => s.id === scriptId);
            if (!script || !programId) return;
            demo.play(scriptId, script.steps.length);
            demoPlayer.start(
                script,
                programId,
                { onStepChange: handleStepChange, onComplete: handleComplete, onNavigate: handleNavigate },
                demo.speed,
            );
            setShowScriptMenu(false);
        },
        [programId, demo, handleStepChange, handleComplete, handleNavigate],
    );

    const handlePause = useCallback(() => {
        demo.pause();
        demoPlayer.pause();
    }, [demo]);

    const handleResume = useCallback(() => {
        demo.resume();
        demoPlayer.resume();
    }, [demo]);

    const handleStop = useCallback(() => {
        demo.stop();
        demoPlayer.stop();
        demoNarration.stop();
        setCurrentStep(null);
    }, [demo]);

    const handleSkipStep = useCallback(() => {
        demoPlayer.nextStep();
    }, []);

    const handleSpeedChange = useCallback(
        (speed: DemoSpeed) => {
            demo.setSpeed(speed);
            demoPlayer.setSpeed(speed);
        },
        [demo],
    );

    if (!isDemoAllowed || !programId) return null;

    const isActive = demo.isPlaying || demo.isPaused;

    return (
        <div className="fixed bottom-4 left-1/2 z-[9990] -translate-x-1/2">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-background/95 px-3 py-2 shadow-lg backdrop-blur-sm">
                {/* Logo / label */}
                <div className="flex items-center gap-1.5 border-r border-border pr-2">
                    <Monitor className="h-3.5 w-3.5 text-primary" />
                    <span className="text-[11px] font-medium text-foreground">Demo</span>
                </div>

                {/* Play / Pause / Stop */}
                {!isActive ? (
                    <div className="relative" ref={menuRef}>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-[11px]"
                            onClick={() => setShowScriptMenu(!showScriptMenu)}
                        >
                            <Play className="h-3 w-3" />
                            Auto-play
                            <ChevronDown className="h-3 w-3" />
                        </Button>
                        {showScriptMenu && (
                            <div className="absolute bottom-full left-0 mb-1 w-[220px] rounded-md border border-border bg-background p-1 shadow-lg">
                                {availableScripts.map((script) => (
                                    <button
                                        key={script.id}
                                        className="flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left hover:bg-muted"
                                        onClick={() => handlePlay(script.id)}
                                    >
                                        <span className="text-[11px] font-medium text-foreground">{script.name}</span>
                                        <span className="text-[11px] text-muted-foreground">{script.description}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {demo.isPlaying ? (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePause} title="Pause">
                                <Pause className="h-3 w-3" />
                            </Button>
                        ) : (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleResume} title="Resume">
                                <Play className="h-3 w-3" />
                            </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleStop} title="Stop">
                            <Square className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleSkipStep} title="Next step">
                            <SkipForward className="h-3 w-3" />
                        </Button>
                    </>
                )}

                {/* Voice narration toggle */}
                {demoNarration.isSupported() && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 border-l border-border pl-2"
                        onClick={() => demoNarration.toggleMute()}
                        title={narrationState === 'muted' ? 'Unmute narration' : 'Mute narration'}
                    >
                        {narrationState === 'muted' ? (
                            <VolumeX className="h-3 w-3 text-muted-foreground" />
                        ) : narrationState === 'speaking' ? (
                            <Volume2 className="h-3 w-3 text-primary" />
                        ) : (
                            <Volume1 className="h-3 w-3 text-muted-foreground" />
                        )}
                    </Button>
                )}

                {/* Speed selector */}
                <div className="flex items-center gap-0.5 border-l border-border pl-2">
                    <Gauge className="h-3 w-3 text-muted-foreground" />
                    {([1, 2, 3] as DemoSpeed[]).map((s) => (
                        <button
                            key={s}
                            className={`rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${demo.speed === s
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-muted'
                                }`}
                            onClick={() => handleSpeedChange(s)}
                        >
                            {s}x
                        </button>
                    ))}
                </div>

                {/* Step progress */}
                {isActive && (
                    <div className="flex items-center gap-1.5 border-l border-border pl-2">
                        {currentStep?.badge && (
                            <Badge variant="outline" className="text-[11px]">
                                {currentStep.badge}
                            </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground">
                            {demo.currentStep + 1} / {demo.totalSteps}
                        </span>
                        {/* Mini progress bar */}
                        <div className="h-1 w-16 overflow-hidden rounded-full bg-muted">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-300"
                                style={{ width: `${((demo.currentStep + 1) / Math.max(demo.totalSteps, 1)) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* What-If toggle */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-[11px] border-l border-border pl-2"
                    onClick={() => setWhatIfOpen((o) => !o)}
                    title="What-If Simulation"
                >
                    <FlaskConical className="h-3 w-3" />
                    What-If
                </Button>
            </div>

            {/* What-If Panel */}
            <WhatIfPanel programId={programId} open={whatIfOpen} onClose={() => setWhatIfOpen(false)} />
        </div>
    );
}
